// What this case is being charged for.
//
// OT does not price surgery — the EMR's own Operation Theatre services do, and a
// charge raised here posts through them. A theatre charge is billed per 30
// minutes of room time; a procedure charge carries the amount entered against it.
//
// Nothing here blocks clinical work. A charge that cannot post is kept with the
// reason on it, and the unposted rows are the retry queue — a case can close and
// be fully documented with its billing outstanding.
//
// Discount and waive are recorded as INTENT. The concession itself is applied at
// the billing desk, which is the one place that owns them.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";

import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
} from "../../../ipd/utils";
import { formatIstDateTime, formatIstTime } from "../../../../utils/istDateTime";
import {
  applyConcession,
  fetchBillingParameters,
  fetchCaseBilling,
  fetchTeam,
  raiseCaseCharge,
  raiseCharges,
  retryCaseCharge,
  reverseCaseCharge,
  reverseCaseCharges,
} from "../../api/otApi";
import { Field, FieldGrid, OtSelect, errorMessage, report } from "../common";
import ChargeServiceAutocomplete from "../../../ot-charge-capture/ChargeServiceAutocomplete";
import { OT_CHARGE_CATEGORIES } from "../../constants";
import { casePractitioners, practitionerRequiredFor } from "../../utils/practitioners";

export default function BillingPanel({ otCase, onChanged, active = true }) {
  const [data, setData] = useState(null);
  const [parameters, setParameters] = useState([]);
  // Who this case can bill a fee to. Loaded with the billing record because the
  // picker needs it the moment the raise dialog opens.
  const [team, setTeam] = useState(null);
  const [error, setError] = useState("");
  const [raising, setRaising] = useState(false);
  const [reversing, setReversing] = useState(null);
  const [reversingAll, setReversingAll] = useState(false);
  const [addingAll, setAddingAll] = useState(false);
  const [concession, setConcession] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const [b, p, t] = await Promise.all([
        fetchCaseBilling(otCase.id),
        fetchBillingParameters({ active_only: 1 }),
        // A team that will not load must not blank the billing tab; the picker
        // still has the case's own surgeon and anaesthetist to offer.
        fetchTeam(otCase.id).catch(() => null),
      ]);
      setData(b);
      setParameters(p || []);
      setTeam(t);
    } catch (err) {
      setError(errorMessage(err, "Could not load the billing record"));
    }
  }, [otCase.id]);

  // Every case tab stays mounted, so what it read once goes stale when another
  // tab changes the case (consent, checklist, notes): re-read on opening.
  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const refreshAll = async () => {
    await load();
    onChanged();
  };

  if (error) return <Typography sx={{ color: IPD_COLORS.error.text }}>{error}</Typography>;

  const charges = data?.charges || [];
  const practitioners = casePractitioners(otCase, team);

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      {!data?.billable && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: IPD_COLORS.warning.bg,
            color: IPD_COLORS.warning.text,
            fontSize: 13,
          }}
        >
          Financial Billing Notice: Case is currently unlinked from an active inpatient billing folio. Associate an Inpatient Admission ID in Case Overview to post theatre debits and implant invoices.
        </Box>
      )}

      <ConsignmentImplantBillingCard otCase={otCase} />

      <IpdSectionCard
        title="Operation Theatre Charge Capture"
        subtitle="Itemized surgical tariffs, theatre running minutes, and professional practitioner charges posted to the patient ledger."
        actions={
          <Button size="small" variant="contained" onClick={() => setRaising(true)}>
            Post Surgical Charge
          </Button>
        }
      >
        {charges.length === 0 ? (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
            No surgical or theatre charges posted yet for this case.
          </Typography>
        ) : (
          charges.map((c) => (
            <Box
              key={c.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.2,
                borderBottom: `1px solid ${IPD_COLORS.border}`,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                  {c.service_name || c.service_code} · {c.charge_kind}
                </Typography>
                {c.practitioner_name && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: IPD_COLORS.textSecondary }}>
                    {c.practitioner_name}
                    {c.practitioner_role ? ` (${c.practitioner_role})` : ""}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  {c.duration_minutes ? `${c.duration_minutes} min · ` : ""}
                  {c.unit_price ? `${c.unit_price}` : "no amount"}
                  {c.discount_amount ? ` · discount ${c.discount_amount} (${c.discount_reason})` : ""}
                  {c.waived ? ` · WAIVED (${c.waive_reason})` : ""}
                  {c.reversed ? ` · REVERSED (${c.reversed_reason})` : ""}
                  {c.posting_error ? ` · ${c.posting_error}` : ""}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: c.posted ? IPD_COLORS.success.text : IPD_COLORS.warning.text,
                }}
              >
                {c.posted ? `POSTED ${formatIstDateTime(c.posted_at)}` : "NOT POSTED"}
              </Typography>
              <Button size="small" onClick={() => setConcession(c)}>
                Concession
              </Button>
              {!c.posted && (
                <Button
                  size="small"
                  startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                  onClick={async () => {
                    report(await retryCaseCharge(c.id));
                    refreshAll();
                  }}
                >
                  Retry
                </Button>
              )}
              {/* OT could raise a charge and had no way to take one back, so a
                  charge made in error had to be chased to the billing desk
                  while ot_case_charge went on showing it as posted. Nothing is
                  deleted: a linked CANCELLATION backs the amount out. */}
              {!c.waived && !c.reversed && (
                <Button
                  size="small"
                  sx={{ color: IPD_COLORS.error.text }}
                  onClick={() => setReversing(c)}
                >
                  Reverse
                </Button>
              )}
          </Box>
        ))
      )}

        <Box sx={{ mt: 2.5 }}>
          <FieldGrid columns={4}>
            <Field label="Charge lines" value={data?.count ?? 0} />
            <Field label="Not yet posted" value={data?.unposted_count ?? 0} />
            <Field label="Items used" value={data?.consumables_total ?? 0} />
            <Field label="Items unbilled" value={data?.consumables_unbilled ?? 0} />
          </FieldGrid>
        </Box>

        {charges.some((c) => c.posted && !c.waived && !c.reversed) && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              sx={{ color: IPD_COLORS.error.text }}
              onClick={() => setReversingAll(true)}
            >
              Bulk Reversal of All Case Charges
            </Button>
          </Box>
        )}
      </IpdSectionCard>

      {/* WHAT THIS CASE HAS INCURRED AND NOT BEEN CHARGED FOR. Derived from
          the record — the clock, the procedure, the anaesthesia note — so the
          bill is not assembled from someone's memory at the end of a long
          list. Only what the record already states is offered; equipment and
          assistants are not guessed at. */}
      {(data?.suggestions || []).length > 0 && (
        <IpdSectionCard
          title="Unbilled Surgical Events & Accruals"
          subtitle="System-generated billable items derived from surgical milestones, timing, and clinical records."
          actions={
            (data.suggestions || []).some((x) => !x.blocked) && (
              <Button
                size="small"
                variant="contained"
                disabled={addingAll}
                onClick={async () => {
                  setAddingAll(true);
                  try {
                    const res = await raiseCharges(otCase.id, {
                      charges: (data.suggestions || [])
                        .filter((x) => !x.blocked)
                        .map((x) => ({
                          charge_kind: x.charge_kind,
                          service_code: x.service_code,
                          service_name: x.service_name,
                          practitioner_id: x.practitioner_id,
                          practitioner_name: x.practitioner_name,
                          practitioner_role: x.practitioner_role,
                          duration_minutes: x.duration_minutes,
                          amount: x.amount,
                        })),
                    });
                    if (report(res)) refreshAll();
                  } finally {
                    setAddingAll(false);
                  }
                }}
              >
                Post All Billable Items
              </Button>
            )
          }
        >
          {(data.suggestions || []).map((x, i) => (
            <Box
              key={x.service_code || i}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
                borderBottom: `1px solid ${IPD_COLORS.border}`,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                  {x.service_name || x.service_code}
                </Typography>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  {x.category}
                  {x.practitioner_name
                    ? ` · ${x.practitioner_name} (${x.practitioner_role})`
                    : ""}
                  {x.duration_minutes ? ` · ${x.duration_minutes} min` : ""}
                  {x.source ? ` · from ${x.source}` : ""}
                </Typography>
              </Box>
              {x.estimated && (
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                  {Number(x.estimated).toLocaleString("en-IN")}
                </Typography>
              )}
              {x.blocked ? (
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.warning.text }}>
                  Charge posting prerequisites pending
                </Typography>
              ) : (
                <Button
                  size="small"
                  onClick={async () => {
                    const res = await raiseCaseCharge(otCase.id, {
                      charge_kind: x.charge_kind,
                      service_code: x.service_code,
                      service_name: x.service_name,
                      practitioner_id: x.practitioner_id,
                      practitioner_name: x.practitioner_name,
                      practitioner_role: x.practitioner_role,
                      duration_minutes: x.duration_minutes,
                      amount: x.amount,
                    });
                    if (report(res)) refreshAll();
                  }}
                >
                  Post Charge
                </Button>
              )}
            </Box>
          ))}
        </IpdSectionCard>
      )}

      {/* WHAT THE ACCOUNT ACTUALLY HOLDS. Everything above is OT's own ledger;
          this is read back from the billing account. They can differ — a charge
          cancelled at the billing desk, or one raised there directly — and
          until now nothing in OT could see that. */}
      <IpdSectionCard
        title="Inpatient Billing Ledger Reconciliation"
        subtitle="Direct reconciliation between Operation Theatre charges and the patient's active inpatient financial folio."
      >
        {data?.account_error ? (
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.warning.text }}>
            {data.account_error}
          </Typography>
        ) : (data?.account_charges || []).length === 0 ? (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
            No charges posted to the patient billing account for this admission yet.
          </Typography>
        ) : (
          (data.account_charges || []).map((a, i) => (
            <Box
              key={a.id || i}
              sx={{
                display: "flex",
                gap: 1.5,
                py: 0.9,
                borderBottom: `1px solid ${IPD_COLORS.border}`,
                flexWrap: "wrap",
                alignItems: "baseline",
              }}
            >
              <Typography sx={{ fontSize: 13, flex: 1, minWidth: 180 }}>
                {a.service_name || a.service_code || "Charge"}
                {(a.practitioner_name || a.metadata?.ot_practitioner_name) && (
                  <Box
                    component="span"
                    sx={{ display: "block", fontSize: 12, color: IPD_COLORS.textSecondary }}
                  >
                    {a.practitioner_name || a.metadata?.ot_practitioner_name}
                  </Box>
                )}
              </Typography>
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                {a.status || ""}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {a.gross_amount ?? a.amount ?? ""}
              </Typography>
            </Box>
          ))
        )}
      </IpdSectionCard>

      {reversing && (
        <ReverseDialog
          charge={reversing}
          caseId={otCase.id}
          onClose={() => setReversing(null)}
          onDone={refreshAll}
        />
      )}
      {reversingAll && (
        <ReverseDialog
          all
          charge={{}}
          caseId={otCase.id}
          onClose={() => setReversingAll(false)}
          onDone={refreshAll}
        />
      )}

      <RaiseChargeDialog
        open={raising}
        caseId={otCase.id}
        parameters={parameters}
        practitioners={practitioners}
        clock={{
          minutes: data?.wheeled_minutes,
          inAt: data?.wheeled_in_at,
          outAt: data?.wheeled_out_at,
        }}
        onClose={() => setRaising(false)}
        onDone={() => {
          setRaising(false);
          refreshAll();
        }}
      />
      <ConcessionDialog
        charge={concession}
        onClose={() => setConcession(null)}
        onDone={() => {
          setConcession(null);
          refreshAll();
        }}
      />
    </Box>
  );
}

/**
 * Reversing a charge, with the reason it needs.
 *
 * A reversed charge with no reason is indistinguishable from a mistake at
 * audit, and theatre charges are the ones that get questioned — so the reason
 * is required rather than encouraged.
 */
function ReverseDialog({ charge, caseId, all, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = all
        ? await reverseCaseCharges(caseId, { reason: reason.trim() })
        : await reverseCaseCharge(charge.id, reason.trim());
      if (report(res)) {
        onClose();
        onDone();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {all ? "Bulk Reversal of All Case Charges" : `Reverse ${charge.service_name || charge.service_code}`}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          Audit Compliance Note: Reversals generate linked credit notes in the patient financial ledger to maintain an unbroken audit trail. Unposted pending charges are removed directly.
        </Typography>
        <IpdFormField label="Clinical / Billing Reversal Justification *">
          <IpdTextField
            fullWidth
            autoFocus
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </IpdFormField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton disabled={busy || !reason.trim()} onClick={run}>
          {busy ? "Reversing…" : "Reverse"}
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}

function RaiseChargeDialog({ open, caseId, parameters, practitioners, clock, onClose, onDone }) {
  const [form, setForm] = useState({
    category: "SURGERY",
    charge_kind: "PROCEDURE",
    billing_parameter_id: "",
    service_code: "",
    practitioner_id: "",
    practitioner_name: "",
    practitioner_role: "",
    amount: "",
    duration_minutes: "",
    remarks: "",
  });
  // The chosen master row, kept whole so the picker can show it back and the
  // 30-minute rate stays visible beside the duration.
  const [service, setService] = useState(null);

  useEffect(() => {
    if (open) {
      setService(null);
      setForm({
        category: "SURGERY",
        charge_kind: "PROCEDURE",
        billing_parameter_id: "",
        service_code: "",
        practitioner_id: "",
        practitioner_name: "",
        practitioner_role: "",
        amount: "",
        duration_minutes: "",
        remarks: "",
      });
    }
  }, [open]);

  const category =
    OT_CHARGE_CATEGORIES.find((c) => c.id === form.category) || OT_CHARGE_CATEGORIES[1];
  const isTheatre = category.id === "THEATRE";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Post Operation Theatre Charge</DialogTitle>
      <DialogContent>
        <IpdFormField label="Charge Category / Service Class">
          <OtSelect
            value={form.category}
            onChange={(e) => {
              // A service picked from one catalogue is not valid in another,
              // so the choice and its amount are cleared rather than carried.
              setService(null);
              const next = OT_CHARGE_CATEGORIES.find((c) => c.id === e.target.value);
              setForm((f) => ({
                ...f,
                category: e.target.value,
                charge_kind: next ? next.kind : "OTHER",
                service_code: "",
                // A fee belongs to a person under one category, not another:
                // carrying the name across would attribute a theatre room.
                practitioner_id: "",
                practitioner_name: "",
                practitioner_role: "",
                amount: "",
              }));
            }}
            options={OT_CHARGE_CATEGORIES.map((c) => ({
              value: c.id,
              label: c.label,
            }))}
          />
          <Typography sx={{ mt: 0.5, fontSize: 11.5, color: IPD_COLORS.textSecondary }}>
            {category.hint}
          </Typography>
        </IpdFormField>

        <Box sx={{ mt: 2 }}>
          <IpdFormField label="Billing parameter (optional)">
            <OtSelect
              placeholder="None — enter the service directly"
              value={form.billing_parameter_id}
              onChange={(e) => setForm((f) => ({ ...f, billing_parameter_id: e.target.value }))}
              options={parameters.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.charge_type}${p.charge ? ` · ${p.charge}` : ""})`,
              }))}
            />
          </IpdFormField>
        </Box>

        <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          {/* The service code was a free-text box somebody had to fill from
              memory -- OT-ROOM-CARDIAC, exactly -- which is the single reason
              no OT charge was ever raised. It searches the service master now,
              and the list narrows to rooms or surgeries by what is being
              charged. */}
          <ChargeServiceAutocomplete
            label={category.label}
            required
            placeholder={`Search ${category.label.toLowerCase()}…`}
            classificationName={category.classification}
            value={service}
            onChange={(item) => {
              setService(item);
              setForm((f) => ({
                ...f,
                service_code: item ? item.service_code || "" : "",
                service_name: item ? item.name : "",
                // The catalogue rate, filled in so nobody retypes it. Still
                // editable — every OT service is charge_modifiable.
                amount:
                  item && item.fee != null && category.id !== "THEATRE"
                    ? String(item.fee)
                    : f.amount,
              }));
            }}
          />
          {isTheatre ? (
            <IpdFormField label="Duration (minutes)">
              <IpdTextField
                fullWidth
                type="number"
                placeholder={clock?.minutes ? String(clock.minutes) : ""}
                value={form.duration_minutes}
                onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))}
              />
              {/* The clock is shown rather than silently used, so a
                  biller can see what they are agreeing with — or
                  disagreeing with. Leaving the box empty takes it. */}
              {clock?.minutes ? (
                <Typography sx={{ mt: 0.5, fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                  In room {formatIstTime(clock.inAt)}, out of room {formatIstTime(clock.outAt)}
                  {" — "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {clock.minutes} min
                  </Box>
                  {". Leave blank to bill that."}
                </Typography>
              ) : (
                <Typography sx={{ mt: 0.5, fontSize: 11.5, color: IPD_COLORS.warning.text }}>
                  No in-room or out-of-room time recorded for this case, so the
                  duration has to be entered.
                </Typography>
              )}
            </IpdFormField>
          ) : (
            <IpdFormField label="Amount">
              <IpdTextField
                fullWidth
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </IpdFormField>
          )}
        </Box>

        {practitionerRequiredFor(form.category) && (
          <Box sx={{ mt: 2 }}>
            <IpdFormField label="Whose fee is this?" required>
              <OtSelect
                placeholder="Choose from the case team"
                value={form.practitioner_name}
                onChange={(e) => {
                  const chosen = (practitioners || []).find((p) => p.name === e.target.value);
                  setForm((f) => ({
                    ...f,
                    practitioner_name: e.target.value,
                    practitioner_id: chosen ? chosen.id : "",
                    practitioner_role: chosen ? chosen.role : "",
                  }));
                }}
                options={(practitioners || []).map((p) => ({
                  value: p.name,
                  label: `${p.name}${p.role ? ` (${p.role})` : ""}`,
                }))}
              />
              {(practitioners || []).length === 0 && (
                <Typography sx={{ mt: 0.5, fontSize: 11.5, color: IPD_COLORS.warning.text }}>
                  Nobody is recorded on this case yet. Add the operating team on
                  the Overview tab — a fee cannot be billed to nobody.
                </Typography>
              )}
            </IpdFormField>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <IpdFormField label="Remarks">
            <IpdTextField
              fullWidth
              value={form.remarks}
              onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
            />
          </IpdFormField>
        </Box>

        <Typography sx={{ mt: 2, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          {isTheatre
            ? "Theatre time is priced per 30 minutes by the OT room service."
            : "A procedure carries no preset fee — enter the amount agreed for it."}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton
          disabled={practitionerRequiredFor(form.category) && !form.practitioner_name}
          onClick={async () => {
            if (report(await raiseCaseCharge(caseId, form))) onDone();
          }}
        >
          Raise
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}

function ConcessionDialog({ charge, onClose, onDone }) {
  const [form, setForm] = useState({
    discount_amount: "",
    discount_reason: "",
    waived: false,
    waive_reason: "",
  });

  useEffect(() => {
    setForm({ discount_amount: "", discount_reason: "", waived: false, waive_reason: "" });
  }, [charge]);

  if (!charge) return null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Discount or waive — {charge.service_name || charge.service_code}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          This records the intent. The concession itself is applied at the billing desk.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <IpdFormField label="Discount amount">
            <IpdTextField
              fullWidth
              value={form.discount_amount}
              onChange={(e) => setForm((f) => ({ ...f, discount_amount: e.target.value }))}
            />
          </IpdFormField>
          <IpdFormField label="Reason for the discount">
            <IpdTextField
              fullWidth
              value={form.discount_reason}
              onChange={(e) => setForm((f) => ({ ...f, discount_reason: e.target.value }))}
            />
          </IpdFormField>
          <IpdFormField label="Waive the charge?">
            <OtSelect
              value={form.waived ? "yes" : "no"}
              onChange={(e) => setForm((f) => ({ ...f, waived: e.target.value === "yes" }))}
              options={[
                { value: "no", label: "No" },
                { value: "yes", label: "Yes — waive it entirely" },
              ]}
            />
          </IpdFormField>
          <IpdFormField label="Reason for the waive">
            <IpdTextField
              fullWidth
              disabled={!form.waived}
              value={form.waive_reason}
              onChange={(e) => setForm((f) => ({ ...f, waive_reason: e.target.value }))}
            />
          </IpdFormField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton
          onClick={async () => {
            const payload = {};
            if (form.discount_amount) {
              payload.discount_amount = form.discount_amount;
              payload.discount_reason = form.discount_reason;
            }
            if (form.waived) {
              payload.waived = true;
              payload.waive_reason = form.waive_reason;
            }
            if (report(await applyConcession(charge.id, payload))) onDone();
          }}
        >
          Record
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Consignment Surgical Implants Billing Card (NPPA DPCO 2013 Disclosure).
 *
 * Discloses consignment implant charges separately from general hospital room/procedure charges,
 * detailing component brand, batch, serial, and NPPA compliance.
 */
function ConsignmentImplantBillingCard({ otCase }) {
  const info = otCase?.consignment_info;
  if (!info || !info.implant_required) return null;

  const details = info.implant_details || {};
  const items = Array.isArray(details.items) ? details.items : (details.name ? [details] : []);
  const isCharged = info.implant_status === "CHARGED";
  // Received into the OT store, but billing did not take the charge.
  const isReceived = info.implant_status === "RECEIVED";

  return (
    <IpdSectionCard
      title="Consignment Surgical Implants (NPPA DPCO 2013 Disclosure)"
      subtitle={`Separate itemized billing disclosure · PO: ${info.consignment_po_number || "PO-CONSIGN"} · GRN: ${info.consignment_grn_number || "Pending"} · NPPA Price Cap Compliant`}
      actions={
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: isCharged ? "#ECFDF5" : "#FEF3C7", color: isCharged ? "#047857" : "#B45309", px: 1.2, py: 0.4, borderRadius: 1, fontWeight: 700, fontSize: 12 }}>
          {isCharged
            ? "✓ CHARGED TO IPD LEDGER"
            : isReceived
            ? "RECEIVED · CHARGE NOT POSTED"
            : "AWAITING INTRA-OP IMPLANTATION"}
        </Box>
      }
    >
      <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 1.5 }}>
        Under NPPA notifications and the Drugs (Prices Control) Order, 2013, orthopaedic implants and consumables are billed transparently with statutory trade margins and ceiling price compliance:
      </Typography>

      <Box sx={{ display: "grid", gap: 1 }}>
        {items.map((it, idx) => (
          <Box key={idx} sx={{ p: 1.2, bgcolor: "#F8FAFC", border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {it.name || it.item_name} × {it.quantity}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textSecondary }}>
                {it.specifications || it.specs || "Standard clinical specification"}
              </Typography>
            </Box>
            {isCharged ? (
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#047857" }}>
                  ₹{Number(details.unit_price || info.tentative_cost || 0).toLocaleString("en-IN")}
                </Typography>
                <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                  Batch: {details.batch_no || "VERIFIED"} · SN: {details.serial_no || "VERIFIED"}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  Tentative Quote: ₹{Number(info.tentative_cost || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {Boolean(info.nppa_compliance || details.nppa_compliance) && (
        (() => {
          const comp = info.nppa_compliance || details.nppa_compliance;
          const isComp = comp.is_compliant !== false && comp.status_badge !== "EXCEEDS_CEILING";
          return (
            <Box
              sx={{
                mt: 1.5,
                p: 1.2,
                borderRadius: 1,
                bgcolor: isComp ? "#ECFDF5" : "#FFFBEB",
                border: `1px solid ${isComp ? "#A7F3D0" : "#FDE68A"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: isComp ? "#047857" : "#B45309",
                  }}
                >
                  {isComp
                    ? "✓ NPPA DPCO 2013 Price Cap Compliant"
                    : "⚠ Exceeds NPPA DPCO 2013 Price Ceiling"}
                </Typography>
                <Typography sx={{ fontSize: 11, color: isComp ? "#065F46" : "#92400E" }}>
                  ({comp.category_label || "Statutory Ceiling Order S.O. 2668(E)"})
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: isComp ? "#047857" : "#B45309",
                }}
              >
                Max Cap: ₹{Number(comp.max_notified_ceiling || 0).toLocaleString("en-IN")}
              </Typography>
            </Box>
          );
        })()
      )}

      {isCharged && (
        <Typography sx={{ mt: 1.5, fontSize: 12, color: "#047857", fontWeight: 600 }}>
          ✓ Billed directly to open admission ledger under Category: OT_CONSUMABLES (Service Code: OT-CONS-IMPLANT) via {info.consignment_grn_number || "GRN-CONSIGN"}.
        </Typography>
      )}
    </IpdSectionCard>
  );
}

